/**
 * A PDF drawn page by page in the app (2026-11-01). Phones cannot show a PDF inside a
 * page: Android Chrome leaves the frame blank and iPhone Safari shows page 1 with no way
 * to scroll. Saad opened his brand work orders on his phone and saw nothing of the brief.
 * Here, on every screen, each page is drawn to fit the screen width, all pages one under the other, with
 * the full PDF a tap away. pdf.js loads only when a PDF is opened this way.
 */
import { useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

export default function PdfPages({ url, download, testid = 'file-pdf' }: { url: string; download?: string; testid?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    let doc: { destroy: () => Promise<void> } | null = null;
    const host = box.current;
    if (!host) return;
    host.replaceChildren();
    setPages(0); setErr('');
    (async () => {
      // legacy build: the modern one needs Safari 17.4+ and drew nothing on older iPhones (2026-11-01)
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const worker = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default;
      pdfjs.GlobalWorkerOptions.workerSrc = worker;
      // one plain GET: no range requests, which the storage CORS rules do not allow
      const pdf = await pdfjs.getDocument({ url, disableRange: true, disableStream: true }).promise;
      doc = pdf;
      if (!live) return;
      setPages(pdf.numPages);
      const width = host.clientWidth || 360;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let n = 1; n <= pdf.numPages && live; n++) {
        const page = await pdf.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const vp = page.getViewport({ scale: (width / base.width) * dpr });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = '100%';
        canvas.className = 'block bg-white rounded shadow';
        canvas.dataset.testid = 'pdf-page';
        host.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;
      }
    })().catch((e) => { console.error('pdf', e); if (live) setErr(String((e as Error)?.message || e).slice(0, 160)); });
    return () => { live = false; doc?.destroy(); };
  }, [url]);

  return (
    <div className="w-[calc(100vw-2rem)] lg:w-[min(960px,calc(100vw-440px))] max-h-[calc(100dvh-160px)] flex flex-col rounded-lg border border-slate-700 bg-slate-950" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-800 text-[11px] text-muted-strong">
        <span>PDF{pages ? ` · ${pages} page${pages === 1 ? '' : 's'}` : ''}</span>
        <a href={download || url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent-400 hover:underline" data-testid="pdf-open-full">
          <Download size={12} /> Open full PDF
        </a>
      </div>
      <div className="flex-1 overflow-auto p-2">
        {!pages && !err && <Loader2 size={22} className="text-accent-400 animate-spin m-10 mx-auto" />}
        {err && <p className="text-[12px] text-amber-300/90 text-center p-6">This PDF cannot be drawn here. Tap "Open full PDF" to read it.<span className="block mt-1 text-[10px] text-muted">{err}</span></p>}
        <div ref={box} className="flex flex-col gap-2" />
      </div>
    </div>
  );
}
